(ns minorodata.components.forms.oauth
  (:require
   ["lucide-react" :refer [CheckCircle ShieldAlert]]
   ["react" :as react]
   [ajax.url]
   [applied-science.js-interop :as js-interop]
   [clojure.string]
   [goog.string :as goog.string]
   [goog.string.format]
   [minorodata.app.handlers :as app.handlers]
   [minorodata.components.spinner.core :as c.spinner]
   [reagent.core :as reagent]
   [reitit.frontend]
   [ui.button])
  (:import
   goog.Uri))


(defn format-request-uri [[uri-format format-data] params]
  (try
   (let [format-params (map (fn [path] (get-in params path))
                            format-data)]
     (apply goog.string/format (cons uri-format format-params)))
   (catch js/Error e
     (js/console.error e)
     uri-format)))


(defn open-oauth-window [{:keys [params oauth-state window-name]}]
  (let [{:keys [request-uri params-processor fields kleene-globals]} params
        redirect-uri (str (js-interop/get-in js/window [:location :origin])
                          "/callback.html")

        request-uri' (if (string? request-uri)
                       request-uri
                       (format-request-uri request-uri (assoc fields :kleene-globals kleene-globals)))

        full-params  (-> {:redirect-uri redirect-uri}
                         (merge params)
                         (assoc :state oauth-state))

        query-params (params-processor full-params)
        auth-uri     (str request-uri' "?" (ajax.url/params-to-str :rails query-params))
        redirect-uri (str (js-interop/get-in js/window [:location :origin])
                          "/redirect.html?"
                          (ajax.url/params-to-str :rails {:auth-uri auth-uri}))]
    (js/window.open redirect-uri window-name)))


(defn sign-in-render [{:keys [configuration field errors id kleene-globals fields]}]
  (let [{:keys [label background-image background-image-height background-image-width
                response-processor has-third-leg oauth-connector-type redirect-uri auth-code-path]
         :or {background-image-height "50px"
              background-image-width "200px"}}
        configuration

        response              (reagent/atom nil)
        status                (reagent/atom :idle)

        error                 (get errors id)
        error-value           (:value error)

        action                (:action field)
        field-value           (:value field)

        oauth-state           (str (random-uuid))
        window-name           oauth-state

        reset-value           (fn []
                                (reset! status :idle)
                                (action nil ""))

        set-error             (fn [error-data]
                                (reset! status :error)
                                (reset! response error-data))

        set-value             (fn [data new-val]
                                (reset! status :idle)
                                (reset! response data)
                                (action nil new-val))

        callback              (fn [data]
                                ;; callback.html did not save the url
                                (cond
                                  (empty? data)
                                  (set-error {:error_description "Failed to complete authentication request"
                                              :error_reason      "no response"})

                                  ;; oauth error (cancelled by user, no permissions to
                                  ;; manage scope, etc.)
                                  (some? (:error data))
                                  (set-error data)

                                  :success
                                  (if has-third-leg
                                    (app.handlers/dispatch
                                     [:connections.api/oauth-third-leg
                                      {:params             {:code          (or (when auth-code-path (get-in data auth-code-path)) (:code data))
                                                            :source-type   oauth-connector-type
                                                            :source-fields (into {} (map (fn [[k {:keys [value]}]] [k value]) fields))
                                                            :configuration (select-keys configuration [:source-auth-type])
                                                            :redirect-uri  (or redirect-uri
                                                                               (str (js-interop/get-in js/window [:location :origin])
                                                                                    "/callback.html"))}
                                       :response-processor    response-processor
                                       :set-error             set-error
                                       :set-value             set-value
                                       :oauth-redirect-params data}])
                                    (set-value data (response-processor data))))

                                ;; cleanup after processing the data
                                (js-interop/call js/localStorage :removeItem window-name))

        event-handler         (react/useCallback
                               (fn [evt]
                                 (when (and (= window-name (js-interop/get evt :key))
                                            (not (nil? (js-interop/get evt :newValue))))
                                   (let [oauth-callback-url (or (js-interop/call js/localStorage :getItem window-name) "")
                                         data               (reitit.frontend/query-params (.parse Uri oauth-callback-url))]
                                     (callback data))))
                               #js [fields])

        sign-in-click-handler (fn []
                                (reset! status :in-progress)
                                (open-oauth-window {:params      (assoc configuration
                                                                          :fields         fields
                                                                          :kleene-globals kleene-globals)
                                                      :window-name window-name
                                                      :oauth-state oauth-state}))]

    (react/useEffect
     (fn []
       (js-interop/call js/window :addEventListener "storage" event-handler)
       #(js-interop/call js/window :removeEventListener "storage" event-handler))
     #js [fields])

    (let [authorized? (and (some? (:value field))
                           (not (clojure.string/blank? (str (:value field)))))]
      [:div {:data-cy  "oauth-login-block"
             :class    "w-full"
             :data-uid window-name}
       (when-not (= :in-progress @status)
         (if authorized?
           [:div {:class "mt-2 mb-2 flex" :data-cy "oauth-status-authorized"}
            [:span {:class "mr-1"}
             [:> CheckCircle {:class "text-kleene-green"}]]
            [:span {:class "text-kleene-green grow mt-1"}
             [:span "Authorized"]]
            [ui.button/button
             {:size     :small
              :variant  :empty
              :on-click reset-value
              :data-cy  "oauth-reset-authorization"}
             "Reset"]]

           [:div {:class "mt-2 mb-2 flex" :data-cy "oauth-status-not-authorized"}
            [:span {:class "mr-1"}
             [:> ShieldAlert {:class "text-kleene-text/50"}]]
            [:span {:class "text-kleene-text/50 mt-1"}
             [:span "Not authorized"]]]))

       (when (and (not= :in-progress @status)
                  (not authorized?))
         [:div {:class "mt-2"}
          [ui.button/button
           (merge {:variant  :primary-border
                   :disabled (= :in-progress @status)
                   :on-click sign-in-click-handler
                   :data-cy  "oauth-login-button"}
                  (when background-image
                    {:style {:width             background-image-width
                             :height            background-image-height
                             :background-image  (str "url(" background-image ")")
                             :background-repeat "no-repeat"
                             :background-size   "contain"}}))
           (when-not background-image label)]])

       (when (= :in-progress @status)
         [:div {:class "mt-1"}
          [:div {:class "flex flex-row mb-2"}
           [:div {:class "mr-2 mt-1"}
            [c.spinner/inline-configurable {:size 16}]]
           [:div
            [:span "Please complete authorization flow"]
            [:br]
            [:span " or cancel and try again"]]]
          [:div {:class "mt-1"}
           [ui.button/button
            {:size     :small
             :variant  :empty
             :on-click #(reset! status :idle)
             :data-cy  "oauth-cancel-authorization"}
            "Cancel authorization"]]])

       (when (= :error @status)
         [:div {:class "mt-2" :data-cy "oauth-login-error"}
          [:span {:class "text-kleene-error-01"}
           "Failed to sign in."
           [:br]
           [:span {:data-cy "oauth-login-error-description"}
            (:error_description @response)]
           "."
           [:br]
           [:span {:data-cy "oauth-login-error-reason"}
            (or
             (:error_reason @response)
             (:error @response))]
           "."]])

       [:p (str error-value)]

       (when (some? error-value)
         [:div {:class "mt-2" :data-cy "oauth-login-validation-error"}
          [:span {:class "text-kleene-error-01"}
           error-value]])])))


(defn sign-in [params]
  ^{:key (str params)}
  [:f> sign-in-render params])


(defn select-values-from-map [data-map keys-to-select]
  (reduce (fn [acc [k v]]
            (assoc acc k (get-in data-map v)))
          keys-to-select
          keys-to-select))


;; Same sign in component but with declarative parameters
(defn sign-in-v2 [params]
  (let [{:keys [configuration]} params

        {:keys [params-keys response-keys]} configuration

        response-processor (fn [resp oauth-redirect-params]
                             (select-values-from-map
                              (merge resp {:oauth-redirect-params oauth-redirect-params})
                              response-keys))

        params-processor   (fn [req-params]
                             (select-values-from-map req-params params-keys))

        params'            (-> params
                               (assoc-in [:configuration :response-processor] response-processor)
                               (assoc-in [:configuration :params-processor]   params-processor))]


    [:f> sign-in-render params']))
